import type { ReactNode } from 'react';
import { CaretRight, Heart, MapPin, Star } from '@phosphor-icons/react';
import { CATEGORIES } from '../../../shared/constants/categories';

export type PlaceCardPlace = {
  id: string;
  type: string;
  name: string;
  address: string;
  image?: string;
  rating?: number | string;
};

type PlaceCardProps = {
  place: PlaceCardPlace;
  favorite?: boolean;
  onToggle: () => void;
  onOpen: () => void;
  favoriteLayout?: boolean;
  className?: string;
  detailLabel?: ReactNode;
  t: Record<string, string>;
};

type ClickEvent = {
  stopPropagation: () => void;
};

export default function PlaceCard({
  place,
  favorite = false,
  onToggle,
  onOpen,
  favoriteLayout = false,
  className = '',
  detailLabel,
  t,
}: PlaceCardProps) {
  const category = CATEGORIES[place.type as keyof typeof CATEGORIES] || CATEGORIES.dining;
  const rating = Number(place.rating) || 0;

  return (
    <article className={`place-card ${className}`.trim()} onClick={onOpen}>
      <div className="card-image">
        <img src={place.image || '/place-placeholder.svg'} alt="" />
        <button
          type="button"
          className={`heart ${favorite ? 'active' : ''}`}
          onClick={(event: ClickEvent) => {
            event.stopPropagation();
            onToggle();
          }}
          aria-label={favorite ? t.removeFavorite : t.addFavorite}
        >
          <Heart weight={favorite ? 'fill' : 'bold'} />
        </button>
        {!favoriteLayout && (
          <span className="category-pill card-category-overlay" style={{ color: category.tone }}>
            <category.Icon weight="fill" />
            {t[place.type]}
          </span>
        )}
      </div>
      <div className="card-body">
        <div>
          {favoriteLayout ? (
            <div className="card-name-row">
              <h3>{place.name}</h3>
              {rating > 0 && (
                <span className="rating">
                  <Star weight="fill" /> {rating.toFixed(1)}
                </span>
              )}
            </div>
          ) : (
            <h3>{place.name}</h3>
          )}
          <p>
            <MapPin weight="fill" />
            <span>{place.address}</span>
          </p>
        </div>
        {!favoriteLayout && rating > 0 && (
          <span className="rating card-rating-body">
            <Star weight="fill" /> {rating.toFixed(1)}
          </span>
        )}
        {favoriteLayout && (
          <span className="card-view-detail">
            {detailLabel || t.viewDetails}
            <CaretRight />
          </span>
        )}
      </div>
    </article>
  );
}
